// src/ryan/components/RyanCardPaymentBrick.tsx
//
// Card Payment Brick do Mercado Pago, escopado só à coleta+tokenização do
// cartão (a escolha entre Pix/cartão já acontece FORA do Brick, na nossa
// própria UI de RyanCheckoutPage.tsx) — nunca recebe número/CVV crus no
// nosso código, o Brick tokeniza dentro do próprio iframe seguro do MP.
// Tema 'dark' + customVariables aproximam a aparência do fundo escuro do
// bloco de pagamento (.rcp-card--payment), sem prender a página a um iframe
// visualmente genérico.
import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { initRyanMercadoPago, resetRyanMercadoPago } from '../config/ryanMercadopago';

export interface RyanCardFormData {
    token: string;
    issuer_id?: string;
    payment_method_id: string;
    installments: number;
    payer: {
        email: string;
        identification?: { type: string; number: string };
    };
}

interface RyanCardPaymentBrickProps {
    amountCents: number;
    payerEmail?: string;
    onSubmit: (data: RyanCardFormData) => Promise<void>;
    onError?: (error: unknown) => void;
}

// Contador módulo-level (não por componente) — garante que CADA montagem do
// Brick, na mesma página, ganhe um id de container genuinamente único. Antes,
// CONTAINER_ID era uma constante fixa reaproveitada por toda remontagem
// (ex: "Trocar forma de pagamento" → volta pro cartão, ou depois de uma
// recusa) — se o unmount() da instância ANTERIOR (assíncrono, e o cleanup do
// useEffect não espera por ele) ainda não tivesse terminado quando a NOVA
// instância tentava criar o Brick no MESMO id de container, as duas
// disputavam o mesmo nó do DOM. É a explicação mais provável encontrada pra
// "botão de pagar travado" e pro "formato inválido" que só aparecia numa
// segunda tentativa, nunca no carregamento limpo da página (Problemas 1 e 3).
let brickInstanceCounter = 0;

const RyanCardPaymentBrick: React.FC<RyanCardPaymentBrickProps> = ({ amountCents, payerEmail, onSubmit, onError }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const brickRef = useRef<any>(null);
    const mountedRef = useRef(true);
    // Um container id NOVO a cada montagem real do componente (não a cada
    // re-render — fica fixo entre re-renders via useState inicializado uma
    // vez), nunca reaproveitado enquanto a página estiver viva.
    const [containerId] = useState(() => `ryan-card-payment-brick-${++brickInstanceCounter}-${Date.now()}`);
    // Token de geração — se uma segunda montagem começar antes da primeira
    // terminar de inicializar (não deveria acontecer com id único, mas serve
    // de segunda camada de proteção), o callback assíncrono da mais antiga
    // se reconhece como obsoleto e não mexe em state/DOM.
    const generationRef = useRef(0);

    useEffect(() => {
        mountedRef.current = true;
        const myGeneration = ++generationRef.current;
        console.log(`[RyanCardPaymentBrick] montando geração ${myGeneration}, container "${containerId}"`);

        const loadBrick = async () => {
            try {
                setLoading(true);
                setError('');

                if (brickRef.current) {
                    try { await brickRef.current.unmount(); } catch { /* instância anterior pode já ter sido destruída */ }
                    brickRef.current = null;
                }

                if (generationRef.current !== myGeneration) {
                    console.log(`[RyanCardPaymentBrick] geração ${myGeneration} obsoleta antes de criar o Brick — abortando`);
                    return;
                }

                const container = document.getElementById(containerId);
                if (!container) { console.warn(`[RyanCardPaymentBrick] container "${containerId}" não encontrado no DOM`); return; }
                container.innerHTML = '';

                const mp = initRyanMercadoPago();
                if (!mp) {
                    if (mountedRef.current) { setError('Erro ao inicializar o formulário de pagamento.'); setLoading(false); }
                    return;
                }

                const brick = await mp.bricks().create('cardPayment', containerId, {
                    initialization: {
                        amount: amountCents / 100,
                        payer: payerEmail ? { email: payerEmail } : undefined,
                    },
                    customization: {
                        visual: {
                            style: {
                                theme: 'dark',
                                customVariables: {
                                    formBackgroundColor: 'transparent',
                                    baseColor: '#2F7130',
                                    textPrimaryColor: '#FFFFFF',
                                    textSecondaryColor: 'rgba(255,255,255,0.65)',
                                    inputBackgroundColor: 'rgba(255,255,255,0.09)',
                                    buttonTextColor: '#0A1F0A',
                                },
                            },
                        },
                    },
                    callbacks: {
                        onReady: () => {
                            console.log(`[RyanCardPaymentBrick] geração ${myGeneration} pronta (onReady)`);
                            if (mountedRef.current && generationRef.current === myGeneration) setLoading(false);
                        },
                        // Resolve/rejeita conforme o resultado de onSubmit (Parte 3.3 do
                        // checkout) — reject só pra falha técnica (rede, 500); um cartão
                        // recusado é resolvido normalmente, exibido depois pelo nosso
                        // próprio estado 'failed', não pela UI de erro genérica do Brick.
                        onSubmit: (formData: RyanCardFormData) => {
                            console.log(`[RyanCardPaymentBrick] geração ${myGeneration} onSubmit disparado`);
                            return onSubmit(formData);
                        },
                        // Confirmado na documentação oficial (Card Payment Brick —
                        // "Additional callbacks" + referência do sdk-js): o único
                        // callback de validação exposto pra ESTE Brick é onBinChange
                        // (dispara a cada dígito no número do cartão, com o BIN
                        // detectado até então). Não existe onValidityChange nem
                        // equivalente por campo — o Brick não expõe se especificamente
                        // o campo de número foi marcado como inválido antes do
                        // onSubmit. Logado aqui pra distinguir, na próxima reprodução
                        // do "formato inválido": se o BIN nunca aparece/fica vazio
                        // (Brick não reconheceu a bandeira enquanto o usuário digitava
                        // — sugere problema de detecção de bandeira/BIN) vs. o BIN
                        // aparece normal e o erro só surge no onSubmit (sugere outra
                        // causa, não relacionada à digitação em si).
                        onBinChange: (bin: string) => {
                            console.log(`[RyanCardPaymentBrick] geração ${myGeneration} onBinChange — bin: "${bin}"`);
                        },
                        onError: (err: unknown) => {
                            console.error(`❌ [RyanCardPaymentBrick] geração ${myGeneration} — erro no Brick:`, err);
                            if (mountedRef.current && generationRef.current === myGeneration) {
                                setError('Erro ao carregar formulário. Tente novamente.');
                                setLoading(false);
                            }
                            onError?.(err);
                        },
                    },
                });

                if (generationRef.current !== myGeneration) {
                    // Uma montagem mais nova já assumiu enquanto create() ainda
                    // estava em voo — destrói esta instância obsoleta em vez de
                    // deixá-la viva concorrendo com a atual.
                    console.log(`[RyanCardPaymentBrick] geração ${myGeneration} obsoleta após criar o Brick — desmontando`);
                    try { await brick.unmount(); } catch { /* ignora — só limpeza defensiva */ }
                    return;
                }

                if (mountedRef.current) brickRef.current = brick;
            } catch (err) {
                console.error(`[RyanCardPaymentBrick] geração ${myGeneration} — falha ao criar o Brick:`, err);
                resetRyanMercadoPago();
                if (mountedRef.current && generationRef.current === myGeneration) {
                    setError('Erro ao carregar formulário de pagamento.');
                    setLoading(false);
                }
                onError?.(err);
            }
        };

        loadBrick();

        return () => {
            console.log(`[RyanCardPaymentBrick] desmontando geração ${myGeneration}`);
            mountedRef.current = false;
            if (brickRef.current) {
                try { brickRef.current.unmount(); } catch { /* cleanup, falha aqui não importa */ }
                brickRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amountCents, payerEmail, containerId]);

    return (
        <div className="rcp-card-brick-wrap">
            {loading && !error && (
                <div className="rcp-card-brick-loading">
                    <div className="rpp-spinner" />
                    <span>Carregando formulário seguro...</span>
                </div>
            )}
            {error && <p className="rpp-modal-error"><AlertCircle size={13} /> {error}</p>}
            {/* Escondido (não desmontado — o Brick já foi criado dentro dele)
                até onReady disparar de verdade. Sem isso, os campos do Brick
                podem renderizar e aceitar toque/autofill do teclado antes de
                a validação interna do MP estar pronta, especialmente em
                conexões mais lentas (mobile) — o que pode gerar "formato
                inválido" pra um cartão correto simplesmente por ele ter sido
                preenchido cedo demais. */}
            <div id={containerId} style={{ display: loading ? 'none' : 'block' }} />
        </div>
    );
};

export default RyanCardPaymentBrick;
